import React, { useState } from "react";
import QRCode from "qrcode";
import { FaEdit, FaTrashAlt, FaInfoCircle, FaWhatsapp, FaPhone, FaEnvelope, FaShareAlt, FaRegCopy, FaQrcode, FaCheck, FaTimes, FaPlus } from "react-icons/fa";
import {
  formatPascal,
  formatEmail,
  formatFullName,
  formatServiceShortcode,
  getServiceShortcodeFromCategories
} from "../utils/formatters";
import AssignedToSelect from "../components/AssignedToSelect";
import NotesPreviewCell from "../components/NotesPreviewCell";
import ServicesPreviewCell from "../components/ServicesPreviewCell";
import {
  digitsOnly,
  joinPhoneDisplay,
  joinPhoneE164,
  normalizeCountryCode,
} from "../utils/phone";

const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

// Legacy clipboard copy for insecure (HTTP) contexts where navigator.clipboard is unavailable
function copyTextFallback(text) {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

const getLeadStatusTone = (status) => {
  const value = String(status || "").trim().toLowerCase();
  if (/(won|qualified|converted|completed|active)/.test(value)) return "success";
  if (/(lost|rejected|cancelled|canceled|closed)/.test(value)) return "danger";
  if (/(contacted|follow|pending|progress|warm)/.test(value)) return "warning";
  if (/(new|open|cold)/.test(value)) return "info";
  return "neutral";
};

const getRealestateStatusTone = getLeadStatusTone;

/**
 * Shared table body renderer for Business Leads, Training Leads, and Customers.
 * The parent decides which rows to pass and what `activeTab` is.
 */
function LeadsTableBody({
  rows,
  activeTab,
  courses,
  domains,
  servicesData = [],
  getColumnWidth,
  getColumnMinWidth,
  handleView,
  handleEdit,
  handleDelete,
  onToggleReFlag,
  leadStatuses = [],
  onUpdateReStatus,
  onUpdateLeadStatus,
  savingStatusLeadId = null,
  assignUsers = [],
  onUpdateAssignedTo,
  savingAssignedLeadId = null,
  onAddReFollowupNote,
  selectedReLeadIds,
  onToggleReLeadSelection
}) {
  const [addingNote, setAddingNote] = useState({ id: null, value: "" });
  const [savingNote, setSavingNote] = useState(false);
  const [propertyLinkModal, setPropertyLinkModal] = useState({
    open: false,
    url: "",
    title: ""
  });
  const [shareModal, setShareModal] = useState({
    open: false,
    title: "",
    text: ""
  });
  const [qrModal, setQrModal] = useState({
    open: false,
    dataUrl: "",
    title: "",
    number: ""
  });
  const safeRows = Array.isArray(rows) ? rows : [];
  const hasEdit = typeof handleEdit === "function";
  const hasDelete = typeof handleDelete === "function";

  const formatRowPhone = (row) => {
    if (!row?.phone || row.phone === "—") return "—";
    const phoneDigits = digitsOnly(row.phone);
    if (!phoneDigits) return "—";

    const storedCc = String(row.countryCode || row.Country_Code || "").trim();
    let cc = "";
    let local = phoneDigits;

    if (storedCc) {
      // Use the row's own country code — never invent +91
      cc = normalizeCountryCode(storedCc, "");
      const ccDigits = digitsOnly(cc);
      if (ccDigits && phoneDigits.startsWith(ccDigits) && phoneDigits.length > ccDigits.length) {
        local = phoneDigits.slice(ccDigits.length);
      } else if (phoneDigits.length > 10) {
        local = phoneDigits.slice(-10);
      }
    } else if (phoneDigits.length > 10) {
      // Only infer a prefix when the phone value itself embeds one
      cc = `+${phoneDigits.slice(0, phoneDigits.length - 10)}`;
      local = phoneDigits.slice(-10);
    }

    return joinPhoneDisplay(cc, local) || "—";
  };

  const getRowTelHref = (row) => {
    if (!row?.phone || row.phone === "—") return null;
    const phoneDigits = digitsOnly(row.phone);
    if (!phoneDigits) return null;

    const storedCc = String(row.countryCode || row.Country_Code || "").trim();
    let cc = "";
    let local = phoneDigits;

    if (storedCc) {
      cc = normalizeCountryCode(storedCc, "");
      const ccDigits = digitsOnly(cc);
      if (ccDigits && phoneDigits.startsWith(ccDigits) && phoneDigits.length > ccDigits.length) {
        local = phoneDigits.slice(ccDigits.length);
      } else if (phoneDigits.length > 10) {
        local = phoneDigits.slice(-10);
      }
    } else if (phoneDigits.length > 10) {
      cc = `+${phoneDigits.slice(0, phoneDigits.length - 10)}`;
      local = phoneDigits.slice(-10);
    }

    if (cc) {
      const e164 = joinPhoneE164(cc, local);
      return e164 ? `tel:${e164}` : null;
    }
    return `tel:${local}`;
  };

  const getRowWhatsAppHref = (row) => {
    if (!row?.phone || row.phone === "—") return null;
    const phoneDigits = digitsOnly(row.phone);
    if (!phoneDigits) return null;

    const storedCc = String(row.countryCode || row.Country_Code || "").trim();
    let cc = "";
    let local = phoneDigits;

    if (storedCc) {
      cc = normalizeCountryCode(storedCc, "");
      const ccDigits = digitsOnly(cc);
      if (ccDigits && phoneDigits.startsWith(ccDigits) && phoneDigits.length > ccDigits.length) {
        local = phoneDigits.slice(ccDigits.length);
      } else if (phoneDigits.length > 10) {
        local = phoneDigits.slice(-10);
      }
    } else if (phoneDigits.length > 10) {
      cc = `+${phoneDigits.slice(0, phoneDigits.length - 10)}`;
      local = phoneDigits.slice(-10);
    }

    // WhatsApp needs an international number — skip if we have no country code
    if (!cc) return null;
    const e164 = joinPhoneE164(cc, local);
    return e164 ? `https://wa.me/${e164.replace(/\D/g, "")}` : null;
  };

  const hideContactEmailColumns =
    activeTab === "Business Leads" ||
    activeTab === "Training Leads" ||
    activeTab === "Job Leads" ||
    activeTab === "Internship Leads";

  const handleShareRealestateLead = async (row) => {
    const shareName = [
      formatPascal(row.firstName || row.first_name),
      formatPascal(row.lastName || row.last_name),
    ].filter(Boolean).join(" ") || String(row.name || "").replace(/,\s*/g, " ").trim();
    const rawCountryCode = String(row.countryCode || "").trim();
    const shareCountryCode =
      rawCountryCode && !rawCountryCode.startsWith("+")
        ? `+${rawCountryCode}`
        : rawCountryCode;
    const title = `Real Estate Lead - ${shareName || `#${row.id}`}`;
    const text = [
      title,
      row.phone && row.phone !== "—"
        ? `Contact: ${[shareCountryCode, row.phone].filter(Boolean).join(" ")}`
        : "",
      row.email && row.email !== "—" ? `Email: ${row.email}` : "",
      row.servicePropertyName && row.servicePropertyName !== "—"
        ? `Property / Service: ${row.servicePropertyName}`
        : "",
      row.propertyType && row.propertyType !== "—" ? `Property Type: ${row.propertyType}` : "",
      row.requirementType && row.requirementType !== "—"
        ? `Requirement Type: ${row.requirementType}`
        : "",
      row.location && row.location !== "—" ? `Location: ${row.location}` : "",
    ].filter(Boolean).join("\n");

    // Always open our own share menu so it works identically on http and https.
    setShareModal({ open: true, title, text });
  };

  const closeShareModal = () => setShareModal({ open: false, title: "", text: "" });

  const startAddNote = (row) => setAddingNote({ id: row.id, value: "" });

  const cancelAddNote = () => setAddingNote({ id: null, value: "" });

  const saveAddNote = async (row) => {
    if (typeof onAddReFollowupNote !== "function") return;
    const value = addingNote.value;
    if (!value || value.trim() === "") return;
    setSavingNote(true);
    try {
      await onAddReFollowupNote(row, value);
      cancelAddNote();
    } finally {
      setSavingNote(false);
    }
  };

  const handleShowMobileQr = async (row) => {
    if (!row?.phone || row.phone === "—") return;
    const phoneDigits = String(row.phone).replace(/\D/g, "");
    const countryDigits = String(row.countryCode || "").replace(/[^\d+]/g, "");
    const telUri = `tel:${`${countryDigits}${phoneDigits}`.replace(/[^\d+]/g, "")}`;
    const shareName = [
      formatPascal(row.firstName || row.first_name),
      formatPascal(row.lastName || row.last_name),
    ].filter(Boolean).join(" ") || String(row.name || "").replace(/,\s*/g, " ").trim();
    try {
      const dataUrl = await QRCode.toDataURL(telUri, { width: 240, margin: 1 });
      setQrModal({
        open: true,
        dataUrl,
        title: shareName || "Lead",
        number: formatRowPhone(row),
      });
    } catch (error) {
      console.error("Error generating mobile QR code:", error);
      alert("Unable to generate QR code for this number.");
    }
  };

  const closeQrModal = () => setQrModal({ open: false, dataUrl: "", title: "", number: "" });

  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: shareModal.title, text: shareModal.text });
      closeShareModal();
    } catch (error) {
      if (error?.name === "AbortError") return;
    }
  };

  const handleCopyShareText = async () => {
    const text = shareModal.text;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        alert("Lead details copied to clipboard.");
        return;
      } catch {
        /* fall through to legacy copy */
      }
    }
    if (copyTextFallback(text)) {
      alert("Lead details copied to clipboard.");
    } else {
      window.prompt("Copy these lead details:", text);
    }
  };

  if (safeRows.length === 0) {
    return null;
  }

  /** Translate shared logical columns to their rendered positions. */
  const custCol = (n) => {
    // Tenants: Company(0), Business(1), Name(2), Tenant type(3);
    // Contact/Location/Services (+3); Status (+2); skip Quote; Notes/Created (+1);
    // then Created By / Updated By / Updated On (+3 more for Source+)
    if (activeTab === "Tenants") {
      if (n >= 1 && n <= 3) return n + 3;
      if (n === 5) return 7;
      if (n === 7 || n === 8) return n + 1;
      if (n >= 9) return n + 4;
      return n;
    }
    if (activeTab === "Realestate Leads") {
      return {
        1: 1, 2: 3, 3: 8, 4: 9, 5: 4, 6: 9,
        7: 12, 8: 13, 9: 14, 10: 15, 11: 16, 12: 17, 13: 17,
      }[n] ?? n;
    }
    return n;
  };

  return (
    <>
      {safeRows.map((row) => (
        <tr key={row.id}>
          {activeTab === "Realestate Leads" && (
            <td className="col-sticky col-check" style={{ minWidth: "42px", width: "42px", textAlign: "center" }}>
              <input
                type="checkbox"
                checked={selectedReLeadIds?.has(String(row.id)) || false}
                onChange={() => onToggleReLeadSelection?.(row.id)}
                onClick={(e) => e.stopPropagation()}
                title="Select lead for export"
                aria-label={`Select ${row.name || "lead"} for export`}
              />
            </td>
          )}
          <td
            className="col-sticky col-0"
            style={{ minWidth: getColumnMinWidth(0) || (activeTab === "Tenants" ? "150px" : activeTab === "Realestate Leads" ? "130px" : "90px"), width: getColumnWidth(0) }}
          >
            {activeTab === "Tenants" ? (
              <>
                <span
                  className="table-name-link"
                  onClick={() => handleView(row.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleView(row.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {formatPascal(row.companyName) && formatPascal(row.companyName) !== "—" ? (
                    formatPascal(row.companyName)
                  ) : (
                    <span className="muted-text">—</span>
                  )}
                </span>
              </>
            ) : (
            <span
              className="table-name-link"
              onClick={() => handleView(row.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleView(row.id);
                }
              }}
              role="button"
              tabIndex={0}
            >
              {(() => {
                const displayName = formatFullName(
                  row.name,
                  row.firstName || row.first_name,
                  row.lastName || row.last_name
                );
                return displayName && displayName !== "—" ? (
                  displayName
                ) : (
                  <span className="muted-text">—</span>
                );
              })()}
            </span>
            )}
          </td>
          {activeTab === "Business Leads" && (
            <td
              style={{ minWidth: getColumnMinWidth(1) || "140px", width: getColumnWidth(1) }}
              className={!row.phone || row.phone === "—" ? "muted-text" : ""}
            >
              {getRowTelHref(row) ? (
                <a
                  className="table-phone-link"
                  href={getRowTelHref(row)}
                  title={`Call ${[
                    formatPascal(String(row.firstName || row.first_name || "").trim()),
                    formatPascal(String(row.lastName || row.last_name || "").trim()),
                  ]
                    .filter(Boolean)
                    .join(" ") || "lead"}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {formatRowPhone(row)}
                </a>
              ) : (
                formatRowPhone(row)
              )}
            </td>
          )}
          {activeTab === "Tenants" && (
            (() => {
              let domainName = row.domain && row.domain !== "—" ? row.domain : "";
              if (
                (!domainName || domainName === "—") &&
                row.domainId &&
                Array.isArray(domains) &&
                domains.length > 0
              ) {
                const dom = domains.find(
                  (d) => d.id === row.domainId || String(d.id) === String(row.domainId)
                );
                if (dom && dom.name) {
                  domainName = dom.name;
                }
              }
              const displayDomain =
                domainName && domainName.trim() !== "" ? formatPascal(domainName) : "—";
              const isMuted = !displayDomain || displayDomain === "—";
              return (
                <td
                  style={{ minWidth: getColumnMinWidth(1) || "120px", width: getColumnWidth(1) }}
                  className={isMuted ? "muted-text" : ""}
                >
                  {displayDomain}
                </td>
              );
            })()
          )}
          {activeTab === "Tenants" && (
            <td
              style={{ minWidth: getColumnMinWidth(2) || "140px", width: getColumnWidth(2) }}
              className={!formatFullName(row.name, row.firstName || row.first_name, row.lastName || row.last_name) ? "muted-text" : ""}
            >
              {formatFullName(row.name, row.firstName || row.first_name, row.lastName || row.last_name) || "—"}
              {row.ownerBusinessCount > 1 ? (
                <span
                  className="tenant-owner-chip tenant-owner-chip--below"
                  title={`This owner has ${row.ownerBusinessCount} companies`}
                >
                  {row.ownerBusinessCount} Companies
                </span>
              ) : null}
            </td>
          )}
          {activeTab === "Tenants" && (
            <td
              style={{ minWidth: getColumnMinWidth(3) || "110px", width: getColumnWidth(3) }}
              className={
                !(row.customerType || row.customer_type) || (row.customerType || row.customer_type) === "—"
                  ? "muted-text"
                  : ""
              }
            >
              {formatPascal(row.customerType || row.customer_type) || "—"}
            </td>
          )}
          {!hideContactEmailColumns && (
          <td
            style={{ minWidth: getColumnMinWidth(custCol(1)) || (activeTab === "Realestate Leads" ? "150px" : "80px"), width: getColumnWidth(custCol(1)) }}
            className={!row.phone || row.phone === "—" ? "muted-text" : ""}
          >
            {activeTab === "Realestate Leads" ? (
              <div className="re-contact-cell">
                {getRowTelHref(row) ? (
                  <a
                    className="table-phone-link"
                    href={getRowTelHref(row)}
                    title="Call"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {formatRowPhone(row)}
                  </a>
                ) : (
                  <span>{formatRowPhone(row)}</span>
                )}
                {row.phone && row.phone !== "—" && (
                  <button
                    type="button"
                    className="re-contact-qr-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowMobileQr(row);
                    }}
                    title="Show dial QR"
                    aria-label="Show dial QR code"
                  >
                    <FaQrcode size={14} />
                  </button>
                )}
              </div>
            ) : getRowTelHref(row) ? (
              <a
                className="table-phone-link"
                href={getRowTelHref(row)}
                title={`Call ${[
                  formatPascal(String(row.firstName || row.first_name || "").trim()),
                  formatPascal(String(row.lastName || row.last_name || "").trim()),
                ]
                  .filter(Boolean)
                  .join(" ") || "lead"}`}
                onClick={(e) => e.stopPropagation()}
              >
                {formatRowPhone(row)}
              </a>
            ) : (
              formatRowPhone(row)
            )}
          </td>
          )}
          {activeTab === "Realestate Leads" && (
            <td
              style={{ minWidth: getColumnMinWidth(2) || "140px", width: getColumnWidth(2) }}
              className={!row.email || row.email === "—" ? "muted-text" : ""}
            >
              {formatEmail(row.email) || "—"}
            </td>
          )}
          <td
            style={{ minWidth: getColumnMinWidth(custCol(2)) || "100px", width: getColumnWidth(custCol(2)) }}
            className={!row.location || row.location === "—" ? "muted-text" : ""}
          >
            {formatPascal(row.location) || "—"}
          </td>

          {activeTab === "Realestate Leads" && (
            <>
              <td
                style={{ minWidth: getColumnMinWidth(4) || "100px", width: getColumnWidth(4) }}
                className={!row.status || row.status === "—" ? "muted-text" : ""}
              >
                <select
                  className={`re-status-select re-status-select--${getRealestateStatusTone(row.status)}`}
                  value={row.statusId != null ? String(row.statusId) : ""}
                  onChange={(e) =>
                    (onUpdateLeadStatus || onUpdateReStatus)?.(row.id, e.target.value)
                  }
                  disabled={
                    typeof (onUpdateLeadStatus || onUpdateReStatus) !== "function" ||
                    String(savingStatusLeadId) === String(row.id)
                  }
                  onClick={(e) => e.stopPropagation()}
                  title="Update status"
                >
                  {row.statusId == null && (
                    <option value="">{row.status || "Select status"}</option>
                  )}
                  {leadStatuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(5) || "170px", width: getColumnWidth(5) }}
                className={!row.tenantName ? "muted-text" : ""}
              >
                {row.tenantName || "—"}
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(21) || "170px", width: getColumnWidth(21) }}
                className={!row.customerName ? "muted-text" : ""}
              >
                {row.customerName || "—"}
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(6) || "130px", width: getColumnWidth(6) }}
                className={
                  !row.servicePropertyName || row.servicePropertyName === "—"
                    ? "muted-text"
                    : ""
                }
              >
                {isHttpUrl(row.propertyServiceLink) &&
                row.servicePropertyName &&
                row.servicePropertyName !== "—" ? (
                  <span
                    className="table-name-link"
                    role="button"
                    tabIndex={0}
                    title="Preview property / service"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPropertyLinkModal({
                        open: true,
                        url: row.propertyServiceLink,
                        title: row.servicePropertyName
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPropertyLinkModal({
                          open: true,
                          url: row.propertyServiceLink,
                          title: row.servicePropertyName
                        });
                      }
                    }}
                  >
                    {row.servicePropertyName}
                  </span>
                ) : (
                  row.servicePropertyName || "—"
                )}
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(18) || "120px", width: getColumnWidth(18) }}
                className={!row.propertyLocation || row.propertyLocation === "—" ? "muted-text" : ""}
              >
                {formatPascal(row.propertyLocation) || "—"}
              </td>
            </>
          )}

          {/* Property Type is immediately before Requirement Type for RE leads. */}
          {activeTab === "Realestate Leads" && (
            <td
              style={{ minWidth: getColumnMinWidth(7) || "100px", width: getColumnWidth(7) }}
              className={!row.propertyType || row.propertyType === "—" ? "muted-text" : ""}
            >
              {formatPascal(row.propertyType) || "—"}
            </td>
          )}

          {/* Services / Courses column */}
          <td
            className={`col-services${activeTab === "Tenants" ? " services-preview-td" : ""}`}
            style={{
              minWidth: getColumnMinWidth(custCol(3)) || (activeTab === "Tenants" ? "50ch" : "120px"),
              width: getColumnWidth(custCol(3)) || (activeTab === "Tenants" ? "50ch" : undefined),
              maxWidth: activeTab === "Tenants" ? "50ch" : undefined,
            }}
          >
            {activeTab === "Tenants" ? (
              <ServicesPreviewCell
                services={row.services}
                title="Services"
              />
            ) : activeTab === "Training Leads" ? (
              (() => {
                const tl = row?.training_lead || row?.trainingLead || row;
                const coursesArr = Array.isArray(row?.Courses)
                  ? row.Courses
                  : Array.isArray(tl?.Courses)
                  ? tl.Courses
                  : [];

                // Prefer structured Courses array when available
                if (coursesArr && coursesArr.length > 0) {
                  const fullNamesArr = [];
                  const shortArr = [];

                  coursesArr.forEach((c) => {
                    const code = c.Course_Code || c.course_code || "";
                    const name = c.Course_Name || c.course_name || "";
                    const full = `${code}${
                      code && name ? " - " : ""
                    }${name}`.trim();
                    if (full) fullNamesArr.push(full);
                    const shortCode = code || formatServiceShortcode(name);
                    if (shortCode) shortArr.push(shortCode);
                  });

                  if (shortArr.length > 0) {
                    return (
                      <span className="services-cell">
                        <FaInfoCircle
                          className="services-info-icon"
                          title={fullNamesArr.join(", ")}
                        />
                        {shortArr.join(", ")}
                      </span>
                    );
                  }
                }

                // Fallbacks: use any direct string fields
                const direct =
                  row.courseCode ||
                  row.courseName ||
                  row.course ||
                  row.services;

                if (direct && direct !== "—") {
                  const parts = String(direct)
                    .split(",")
                    .map((p) => p.trim())
                    .filter(Boolean);
                  const fullNames = parts.join(", ");
                  const shortcodes = parts.map((p) => {
                    const [maybeCode, maybeName] = p.split(" - ").map((x) => x.trim());
                    if (maybeCode && maybeName) {
                      return maybeCode;
                    }
                    return formatServiceShortcode(p);
                  });

                  return (
                    <span className="services-cell">
                      <FaInfoCircle
                        className="services-info-icon"
                        title={fullNames}
                      />
                      {shortcodes.join(", ")}
                    </span>
                  );
                }

                if (row.courseId && Array.isArray(courses)) {
                  const course = courses.find((c) => c.Course_Id === row.courseId);
                  if (course) {
                    const code = course.Course_Code || course.course_code || "";
                    const name = course.Course_Name || course.course_name || "";
                    const full = `${code}${
                      code && name ? " - " : ""
                    }${name}`.trim();
                    const shortCode = code || formatServiceShortcode(name);
                    if (shortCode) {
                      return (
                        <span className="services-cell">
                          <FaInfoCircle
                            className="services-info-icon"
                            title={full}
                          />
                          {shortCode}
                        </span>
                      );
                    }
                    if (full) return full;
                  }
                }
                return <span className="muted-text">—</span>;
              })()
            ) : activeTab === "Realestate Leads" ? (
              formatPascal(row.requirementType) || "—"
            ) : row.services && row.services !== "—" ? (
              (() => {
                const arr = Array.isArray(row.services)
                  ? row.services
                  : row.services.split(", ").map((s) => s.trim()).filter(Boolean);
                const fullNames = arr.join(", ");
                const shortcodes = arr.map((s) =>
                  getServiceShortcodeFromCategories(s, servicesData)
                );
                return (
                  <span className="services-cell">
                    <FaInfoCircle className="services-info-icon" title={fullNames} />
                    {shortcodes.join(", ")}
                  </span>
                );
              })()
            ) : (
              <span className="muted-text">—</span>
            )}
          </td>

          {/* Business (domain catalog) / Mode column */}
          {activeTab === "Training Leads" && (
            <td
              style={{ minWidth: getColumnMinWidth(custCol(4)) || "100px", width: getColumnWidth(custCol(4)) }}
              className={!row.mode || row.mode === "—" ? "muted-text" : ""}
            >
              {formatPascal(
                (() => {
                  const tl = row?.training_lead || row?.trainingLead || row;
                  const modesArr = Array.isArray(row?.Modes)
                    ? row.Modes
                    : Array.isArray(tl?.Modes)
                    ? tl.Modes
                    : [];
                  if (Array.isArray(row.mode)) return row.mode.join(", ");
                  const fromRow = row.mode || row.Mode || row.mode_name || row.modeName;
                  if (fromRow) return fromRow;
                  const names = modesArr.map((m) => m.Mode_Name || m.mode_name).filter(Boolean);
                  return names.length > 0 ? names.join(", ") : "";
                })()
              ) || "—"}
            </td>
          )}
          {activeTab !== "Training Leads" && activeTab !== "Job Leads" && activeTab !== "Internship Leads" && activeTab !== "Realestate Leads" && activeTab !== "Tenants" && (
            (() => {
              let domainName = row.domain && row.domain !== "—" ? row.domain : "";
              if (
                (!domainName || domainName === "—") &&
                row.domainId &&
                Array.isArray(domains) &&
                domains.length > 0
              ) {
                const dom = domains.find(
                  (d) => d.id === row.domainId || String(d.id) === String(row.domainId)
                );
                if (dom && dom.name) {
                  domainName = dom.name;
                }
              }
              const displayDomain =
                domainName && domainName.trim() !== "" ? formatPascal(domainName) : "—";
              const isMuted = !displayDomain || displayDomain === "—";
              return (
                <td
                  style={{ minWidth: getColumnMinWidth(custCol(4)) || "100px", width: getColumnWidth(custCol(4)) }}
                  className={isMuted ? "muted-text" : ""}
                >
                  {displayDomain}
                </td>
              );
            })()
          )}

          {/* Status */}
          {activeTab !== "Realestate Leads" && (
            <td
              style={{ minWidth: getColumnMinWidth(custCol(5)) || "100px", width: getColumnWidth(custCol(5)) }}
            >
              {typeof onUpdateLeadStatus === "function" && leadStatuses.length > 0 ? (
                <select
                  className={`re-status-select re-status-select--${getLeadStatusTone(row.status)}`}
                  value={row.statusId != null ? String(row.statusId) : ""}
                  onChange={(e) => onUpdateLeadStatus(row.id, e.target.value)}
                  disabled={String(savingStatusLeadId) === String(row.id)}
                  onClick={(e) => e.stopPropagation()}
                  title="Update status"
                >
                  {row.statusId == null && (
                    <option value="">{row.status && row.status !== "—" ? row.status : "Select status"}</option>
                  )}
                  {leadStatuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              ) : row.status && row.status !== "—" ? (
                <span className={`lead-status-badge lead-status-badge--${getLeadStatusTone(row.status)}`}>
                  {row.status}
                </span>
              ) : (
                <span className="muted-text">—</span>
              )}
            </td>
          )}

          {/* Quote Sent (not for training, internship, job, realestate, or tenants) */}
          {activeTab !== "Training Leads" && activeTab !== "Internship Leads" && activeTab !== "Job Leads" && activeTab !== "Realestate Leads" && activeTab !== "Tenants" && (
            <td
              style={{ minWidth: getColumnMinWidth(custCol(6)) || "100px", width: getColumnWidth(custCol(6)) }}
            >
              {row.isQuoteSent || "No"}
            </td>
          )}

          {/* Sharing Preference + Move-in Date (Realestate only) */}
          {activeTab === "Realestate Leads" && (
            <>
              <td
                style={{ minWidth: getColumnMinWidth(19) || "130px", width: getColumnWidth(19) }}
                className={!row.sharingPreference || row.sharingPreference === "—" ? "muted-text" : ""}
              >
                {formatPascal(row.sharingPreference) || "—"}
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(20) || "120px", width: getColumnWidth(20) }}
                className={!row.moveInDate || row.moveInDate === "—" ? "muted-text" : ""}
              >
                {row.moveInDate || "—"}
              </td>
            </>
          )}

          {/* Site Visit (Realestate only) */}
          {activeTab === "Realestate Leads" && (
            <td style={{ minWidth: "90px", width: getColumnWidth(9), textAlign: "center" }}>
              <input
                type="checkbox"
                checked={!!row.siteVisit}
                disabled={typeof onToggleReFlag !== "function"}
                onChange={(e) => onToggleReFlag?.(row.id, "siteVisit", e.target.checked)}
                title="Site Visit"
              />
            </td>
          )}
          {activeTab === "Realestate Leads" && (
            <>
              <td style={{ minWidth: "100px", width: getColumnWidth(10), textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={!!row.isOpportunity}
                  disabled={typeof onToggleReFlag !== "function"}
                  onChange={(e) => onToggleReFlag?.(row.id, "isOpportunity", e.target.checked)}
                  title="Opportunity"
                />
              </td>
              <td style={{ minWidth: "100px", width: getColumnWidth(11), textAlign: "center" }}>
                <label className="automation-switch" title="Automation">
                  <input
                    type="checkbox"
                    checked={!!row.automationEnabled}
                    disabled={typeof onToggleReFlag !== "function"}
                    onChange={(e) => onToggleReFlag?.(row.id, "automationEnabled", e.target.checked)}
                  />
                  <span className="automation-switch-track" aria-hidden="true">
                    <span className="automation-switch-thumb" />
                  </span>
                </label>
              </td>
            </>
          )}

          {/* Customer Notes (tenants only) */}
          {activeTab === "Tenants" && (
            <td
              className="notes-preview-td"
              style={{
                minWidth: getColumnMinWidth(custCol(7)) || "160px",
                width: getColumnWidth(custCol(7)) || "160px",
                maxWidth: "180px",
              }}
            >
              <NotesPreviewCell
                notes={row.customerNotes ?? row.Notes}
                title="Notes"
              />
            </td>
          )}

          {/* Follow-up & dates (hidden on Tenants — edit on details page) */}
          {activeTab !== "Tenants" && (
            <td
              style={{
                minWidth:
                  getColumnMinWidth(
                    custCol(activeTab === "Training Leads" ? 6 : 7)
                  ) || "140px",
                width: getColumnWidth(
                  custCol(activeTab === "Training Leads" ? 6 : 7)
                )
              }}
              className={!row.lastFollowupDate || row.lastFollowupDate === "—" ? "muted-text" : ""}
            >
              {row.lastFollowupDate || "—"}
            </td>
          )}
          {activeTab !== "Tenants" && (
            <td
              className="notes-preview-td"
              style={{
                minWidth:
                  getColumnMinWidth(
                    custCol(activeTab === "Training Leads" ? 7 : 8)
                  ) || "160px",
                width:
                  getColumnWidth(
                    custCol(activeTab === "Training Leads" ? 7 : 8)
                  ) || "160px",
                maxWidth: "180px",
              }}
            >
              {activeTab === "Realestate Leads" && typeof onAddReFollowupNote === "function" ? (
                addingNote.id === row.id ? (
                  <div className="followup-note-edit">
                    <textarea
                      className="followup-note-input"
                      value={addingNote.value}
                      onChange={(e) => setAddingNote({ id: row.id, value: e.target.value })}
                      rows={2}
                      autoFocus
                      placeholder="New follow-up note"
                    />
                    <div className="followup-note-edit-actions">
                      <button
                        type="button"
                        className="followup-note-save"
                        title="Add follow-up"
                        disabled={savingNote}
                        onClick={() => saveAddNote(row)}
                      >
                        <FaCheck />
                      </button>
                      <button
                        type="button"
                        className="followup-note-cancel"
                        title="Cancel"
                        disabled={savingNote}
                        onClick={cancelAddNote}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="followup-note-view">
                    <NotesPreviewCell notes={row.notes} title="Follow-up Notes" />
                    <button
                      type="button"
                      className="followup-note-edit-btn"
                      title="Add follow-up"
                      onClick={() => startAddNote(row)}
                    >
                      <FaPlus />
                    </button>
                  </div>
                )
              ) : (
                <NotesPreviewCell
                  notes={row.notes}
                  title="Follow-up Notes"
                />
              )}
            </td>
          )}
          {activeTab === "Realestate Leads" ? (
            <td
              style={{
                minWidth: getColumnMinWidth(custCol(9)) || "140px",
                width: getColumnWidth(custCol(9))
              }}
              className={!row.createdBy || row.createdBy === "—" ? "muted-text" : ""}
            >
              {row.createdBy || "—"}
            </td>
          ) : activeTab !== "Tenants" ? (
            <td
              style={{
                minWidth:
                  getColumnMinWidth(
                    custCol(activeTab === "Training Leads" ? 8 : 9)
                  ) || "140px",
                width: getColumnWidth(
                  custCol(activeTab === "Training Leads" ? 8 : 9)
                )
              }}
              className={!row.nextFollowupDate || row.nextFollowupDate === "—" ? "muted-text" : ""}
            >
              {row.nextFollowupDate || "—"}
            </td>
          ) : null}
          {activeTab !== "Tenants" && (
            <td style={{ minWidth: "140px" }}>
              <AssignedToSelect
                value={row.assignedToId ?? ""}
                users={assignUsers}
                disabled={typeof onUpdateAssignedTo !== "function" || String(savingAssignedLeadId) === String(row.id)}
                onChange={(userId) => onUpdateAssignedTo?.(row.id, userId)}
              />
            </td>
          )}
          <td
            style={{
              minWidth:
                getColumnMinWidth(
                  custCol(
                    activeTab === "Training Leads" ? 9 : activeTab === "Tenants" ? 8 : 10
                  )
                ) || (activeTab === "Tenants" ? "120px" : "100px"),
              width: getColumnWidth(
                custCol(activeTab === "Training Leads" ? 9 : activeTab === "Tenants" ? 8 : 10)
              ) || (activeTab === "Tenants" ? "120px" : undefined)
            }}
            className={!row.createdAt || row.createdAt === "—" ? "muted-text" : ""}
          >
            {row.createdAt || "—"}
          </td>
          {activeTab === "Tenants" && (
            <>
              <td
                style={{
                  minWidth: getColumnMinWidth(10) || "120px",
                  width: getColumnWidth(10),
                }}
                className={!row.createdBy || row.createdBy === "—" ? "muted-text" : ""}
              >
                {formatPascal(row.createdBy) || "—"}
              </td>
              <td
                style={{
                  minWidth: getColumnMinWidth(11) || "120px",
                  width: getColumnWidth(11),
                }}
                className={!row.updatedBy || row.updatedBy === "—" ? "muted-text" : ""}
              >
                {formatPascal(row.updatedBy) || "—"}
              </td>
              <td
                style={{
                  minWidth: getColumnMinWidth(12) || "120px",
                  width: getColumnWidth(12) || "120px",
                }}
                className={!row.updatedAt || row.updatedAt === "—" ? "muted-text" : ""}
              >
                {row.updatedAt || "—"}
              </td>
            </>
          )}
          <td
            style={{
              minWidth:
                getColumnMinWidth(
                  custCol(
                    activeTab === "Training Leads" ? 10 : activeTab === "Tenants" ? 9 : 11
                  )
                ) || "100px",
              width: getColumnWidth(
                custCol(activeTab === "Training Leads" ? 10 : activeTab === "Tenants" ? 9 : 11)
              )
            }}
            className={!row.source || row.source === "—" ? "muted-text" : ""}
          >
            {formatPascal(row.source) || "—"}
          </td>

          {/* Company name (not training/RE/tenants — tenants render company next to Full Name) */}
          {activeTab !== "Training Leads" && activeTab !== "Realestate Leads" && activeTab !== "Tenants" && (
            <td
              style={{
                minWidth:
                  getColumnMinWidth(custCol(11)) || "120px",
                width: getColumnWidth(custCol(11))
              }}
              className={!row.companyName || row.companyName === "—" ? "muted-text" : ""}
            >
              {formatPascal(row.companyName) || "—"}
            </td>
          )}

          {/* Email (hidden for tenants — use Action mail buttons) */}
          {activeTab !== "Realestate Leads" && activeTab !== "Tenants" && !hideContactEmailColumns && (
            <td
              style={{
                minWidth:
                  getColumnMinWidth(
                    custCol(
                      activeTab === "Training Leads" ? 11 : 12
                    )
                  ) || "100px",
                width: getColumnWidth(
                  custCol(activeTab === "Training Leads" ? 11 : 12)
                )
              }}
              className={!row.email || row.email === "—" ? "muted-text" : ""}
            >
              {formatEmail(row.email) || "—"}
            </td>
          )}

          {/* Actions */}
          <td
            className="col-sticky col-action"
            style={{
              minWidth:
                getColumnMinWidth(
                  custCol(
                    activeTab === "Training Leads" ? 12 : activeTab === "Tenants" ? 10 : 13
                  )
                ) || (activeTab === "Tenants" ? "220px" : "120px"),
              width: getColumnWidth(
                custCol(activeTab === "Training Leads" ? 12 : activeTab === "Tenants" ? 10 : 13)
              )
            }}
          >
            <div className={`action-buttons ${activeTab === "Realestate Leads" || hideContactEmailColumns ? "re-lead-actions" : ""}`}>
              {(activeTab === "Realestate Leads" || hideContactEmailColumns) && (
                <>
                  {getRowWhatsAppHref(row) ? (
                    <a
                      className="re-lead-action re-lead-action--whatsapp"
                      href={getRowWhatsAppHref(row)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`WhatsApp ${formatRowPhone(row)}`}
                      aria-label="Open WhatsApp"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaWhatsapp size={14} />
                    </a>
                  ) : (
                    <span
                      className="re-lead-action re-lead-action--whatsapp re-lead-action--disabled"
                      title="WhatsApp unavailable"
                      aria-label="WhatsApp unavailable"
                      aria-disabled="true"
                    >
                      <FaWhatsapp size={14} />
                    </span>
                  )}
                  {getRowTelHref(row) ? (
                    <a
                      className="re-lead-action re-lead-action--call"
                      href={getRowTelHref(row)}
                      title={`Call ${formatRowPhone(row)}`}
                      aria-label="Call lead"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                    </a>
                  ) : (
                    <span
                      className="re-lead-action re-lead-action--call re-lead-action--disabled"
                      title="Call unavailable"
                      aria-label="Call unavailable"
                      aria-disabled="true"
                    >
                      <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                    </span>
                  )}
                  {row.email && row.email !== "—" ? (
                    <a
                      className="re-lead-action re-lead-action--email"
                      href={`mailto:${row.email}`}
                      title={`Email ${row.email}`}
                      aria-label="Email lead"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaEnvelope size={12} />
                    </a>
                  ) : (
                    <span
                      className="re-lead-action re-lead-action--email re-lead-action--disabled"
                      title="Email unavailable"
                      aria-label="Email unavailable"
                      aria-disabled="true"
                    >
                      <FaEnvelope size={12} />
                    </span>
                  )}
                </>
              )}
              {activeTab === "Realestate Leads" && (
                <button
                  type="button"
                  className="re-lead-action re-lead-action--share"
                  onClick={() => handleShareRealestateLead(row)}
                  title="Share"
                  aria-label="Share lead"
                >
                  <FaShareAlt size={13} />
                </button>
              )}
              {activeTab !== "Tenants" && hasEdit && (
                <button
                  className={activeTab === "Realestate Leads" || hideContactEmailColumns ? "re-lead-action re-lead-action--edit" : "btn-edit"}
                  onClick={() => handleEdit(row.id)}
                  style={activeTab === "Realestate Leads" || hideContactEmailColumns ? undefined : {
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px 6px",
                    minWidth: 28,
                    minHeight: 28
                  }}
                  title="Edit"
                  aria-label="Edit lead"
                >
                  <FaEdit size={14} />
                </button>
              )}
              {activeTab === "Tenants" && (
                <>
                  {getRowTelHref(row) ? (
                    <a
                      className="btn-edit tenant-action-btn"
                      href={getRowTelHref(row)}
                      title={`Call ${formatRowPhone(row)}`}
                      aria-label="Call tenant"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                    </a>
                  ) : (
                    <span
                      className="btn-edit tenant-action-btn tenant-action-btn--disabled"
                      title="Call unavailable"
                      aria-label="Call unavailable"
                      aria-disabled="true"
                    >
                      <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                    </span>
                  )}
                  {getRowWhatsAppHref(row) ? (
                    <a
                      className="btn-edit tenant-action-btn tenant-action-btn--whatsapp"
                      href={getRowWhatsAppHref(row)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`WhatsApp ${formatRowPhone(row)}`}
                      aria-label="Open WhatsApp"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaWhatsapp size={14} />
                    </a>
                  ) : (
                    <span
                      className="btn-edit tenant-action-btn tenant-action-btn--whatsapp tenant-action-btn--disabled"
                      title="WhatsApp unavailable"
                      aria-label="WhatsApp unavailable"
                      aria-disabled="true"
                    >
                      <FaWhatsapp size={14} />
                    </span>
                  )}
                  {row.email && row.email !== "—" ? (
                    <a
                      className="btn-edit tenant-action-btn tenant-action-btn--mail"
                      href={`mailto:${row.email}`}
                      title={`Personal email ${row.email}`}
                      aria-label="Personal email"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="tenant-mail-icon">
                        <FaEnvelope size={12} />
                        <span className="tenant-mail-badge">P</span>
                      </span>
                    </a>
                  ) : (
                    <span
                      className="btn-edit tenant-action-btn tenant-action-btn--mail tenant-action-btn--disabled"
                      title="Personal email unavailable"
                      aria-label="Personal email unavailable"
                      aria-disabled="true"
                    >
                      <span className="tenant-mail-icon">
                        <FaEnvelope size={12} />
                        <span className="tenant-mail-badge">P</span>
                      </span>
                    </span>
                  )}
                  {row.companyEmail && row.companyEmail !== "—" ? (
                    <a
                      className="btn-edit tenant-action-btn tenant-action-btn--mail"
                      href={`mailto:${row.companyEmail}`}
                      title={`Company email ${row.companyEmail}`}
                      aria-label="Company email"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="tenant-mail-icon">
                        <FaEnvelope size={12} />
                        <span className="tenant-mail-badge">C</span>
                      </span>
                    </a>
                  ) : (
                    <span
                      className="btn-edit tenant-action-btn tenant-action-btn--mail tenant-action-btn--disabled"
                      title="Company email unavailable"
                      aria-label="Company email unavailable"
                      aria-disabled="true"
                    >
                      <span className="tenant-mail-icon">
                        <FaEnvelope size={12} />
                        <span className="tenant-mail-badge">C</span>
                      </span>
                    </span>
                  )}
                  {hasEdit && (
                    <button
                      className="btn-edit tenant-action-btn"
                      onClick={() => handleEdit(row.id)}
                      title="Edit Tenant"
                      aria-label="Edit Tenant"
                    >
                      <FaEdit size={14} />
                    </button>
                  )}
                  {hasDelete && (
                    <button
                      className="btn-delete tenant-action-btn"
                      onClick={() => handleDelete(row.id)}
                      title="Delete Tenant"
                      aria-label="Delete Tenant"
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  )}
                </>
              )}
            </div>
          </td>
        </tr>
      ))}
      {propertyLinkModal.open && (
        <tr>
          <td colSpan={activeTab === "Tenants" ? 15 : activeTab === "Realestate Leads" ? 23 : 16} className="followup-notes-modal-cell">
            <div
              className="followup-notes-modal-overlay"
              onClick={() => setPropertyLinkModal({ open: false, url: "", title: "" })}
            >
              <div
                className="followup-notes-modal property-link-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="followup-notes-modal-header">
                  <h3>{propertyLinkModal.title || "Property / Service"}</h3>
                  <button
                    type="button"
                    onClick={() => setPropertyLinkModal({ open: false, url: "", title: "" })}
                  >
                    Close
                  </button>
                </div>
                <iframe
                  className="property-link-modal-frame"
                  src={propertyLinkModal.url}
                  title={propertyLinkModal.title || "Property / Service Preview"}
                />
              </div>
            </div>
          </td>
        </tr>
      )}
      {qrModal.open && (
        <tr>
          <td colSpan={activeTab === "Tenants" ? 15 : activeTab === "Realestate Leads" ? 23 : 21} className="followup-notes-modal-cell">
            <div className="followup-notes-modal-overlay" onClick={closeQrModal}>
              <div
                className="followup-notes-modal re-qr-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="followup-notes-modal-header">
                  <h3>{qrModal.title}</h3>
                  <button type="button" onClick={closeQrModal}>
                    Close
                  </button>
                </div>
                <div className="re-qr-modal-body">
                  {qrModal.dataUrl && (
                    <img src={qrModal.dataUrl} alt={`QR code to dial ${qrModal.number}`} />
                  )}
                  <p className="re-qr-modal-number">{qrModal.number}</p>
                  <p className="re-qr-modal-hint">Scan with a phone camera to dial this number.</p>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      {shareModal.open && (
        <tr>
          <td colSpan={activeTab === "Tenants" ? 15 : activeTab === "Realestate Leads" ? 23 : 16} className="followup-notes-modal-cell">
            <div className="followup-notes-modal-overlay" onClick={closeShareModal}>
              <div
                className="followup-notes-modal lead-share-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="followup-notes-modal-header">
                  <h3>Share link</h3>
                  <button type="button" onClick={closeShareModal}>
                    Close
                  </button>
                </div>
                <div className="lead-share-modal-body">
                  <p className="lead-share-title" title={shareModal.title}>
                    {shareModal.title}
                  </p>
                  <div className="lead-share-preview">{shareModal.text}</div>
                  <div className="lead-share-options">
                    <a
                      className="lead-share-option whatsapp"
                      href={`https://wa.me/?text=${encodeURIComponent(shareModal.text)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeShareModal}
                    >
                      <FaWhatsapp /> WhatsApp
                    </a>
                    <a
                      className="lead-share-option email"
                      href={`mailto:?subject=${encodeURIComponent(shareModal.title)}&body=${encodeURIComponent(shareModal.text)}`}
                      onClick={closeShareModal}
                    >
                      <FaEnvelope /> Email
                    </a>
                    <button
                      type="button"
                      className="lead-share-option copy"
                      onClick={handleCopyShareText}
                    >
                      <FaRegCopy /> Copy details
                    </button>
                    {typeof navigator !== "undefined" && navigator.share && (
                      <button
                        type="button"
                        className="lead-share-option more"
                        onClick={handleNativeShare}
                      >
                        <FaShareAlt /> More apps
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default LeadsTableBody;

